import React, { useEffect, useState, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Scan, Loader2, Check, ListChecks, Boxes, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import UsnStickerPrinter from '@/components/print/UsnStickerPrinter';
import { JwEwbBar } from '@/components/jobWork/JwTransfer';

const JwTaggingStation = () => {
  const { jwId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [replacements, setReplacements] = useState([]);
  const [activeComponent, setActiveComponent] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [usn, setUsn] = useState('');
  const [scanning, setScanning] = useState(false);
  const [marking, setMarking] = useState(false);
  const usnRef = useRef(null);
  const [bundleScan, setBundleScan] = useState('');
  const [taggingBundle, setTaggingBundle] = useState(false);
  const [bundleResult, setBundleResult] = useState(null);
  const bundleRef = useRef(null);
  const [printGroups, setPrintGroups] = useState([]);
  const [loadingPrint, setLoadingPrint] = useState(false);

  const refresh = async () => {
    const r = await apiClient.get(`/flexi/jw-orders/${jwId}`);
    setOrder(r.data.order);
    setReplacements(r.data.replacements || []);
  };
  useEffect(() => { refresh(); }, [jwId]);
  useEffect(() => { if (activeComponent && usnRef.current) usnRef.current.focus(); }, [activeComponent]);

  const scan = async (e) => {
    e?.preventDefault?.();
    const u = usn.trim();
    if (!u || !activeComponent) return;
    setScanning(true);
    try {
      const res = await apiClient.post('/flexi/jw-orders/tag-usn', {
        jw_id: jwId, component_item: activeComponent,
        original_usn: u, bundle_code: bundleCode || null,
      });
      toast.success(`${u} → ${res.data.new_usn}`);
      setUsn('');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Tag failed'); }
    finally { setScanning(false); }
  };

  const tagBundle = async (e) => {
    e?.preventDefault?.();
    const code = bundleScan.trim();
    if (!code) return;
    setTaggingBundle(true);
    try {
      const res = await apiClient.post('/flexi/jw-orders/tag-bundle', { jw_id: jwId, bundle_code: code });
      setBundleResult(res.data);
      if (res.data.mode === 'whole_bundle') {
        if (res.data.minted_count > 0) {
          toast.success(`Consumed ${res.data.consumed} ${res.data.unit} → minted ${res.data.minted_count} assembly USN(s) (${res.data.total_minted}/${res.data.target_qty})`);
        } else {
          toast.info(`Consumed ${res.data.consumed} ${res.data.unit} — not enough for a whole assembly unit yet`);
        }
      } else if (res.data.tagged_count > 0) {
        toast.success(`Tagged ${res.data.tagged_count} USN(s) from ${code}`);
      } else if (!(res.data.warnings || []).length) {
        toast.info('Nothing tagged');
      }
      (res.data.warnings || []).forEach((w) => toast.warning(w));
      setBundleScan('');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bundle tag failed'); }
    finally { setTaggingBundle(false); setTimeout(() => bundleRef.current?.focus(), 40); }
  };

  const loadPrintUsns = async () => {
    setLoadingPrint(true);
    try {
      const res = await apiClient.get(`/flexi/jw-orders/${jwId}/print-usns`);
      setPrintGroups(res.data.groups || []);
      if (!(res.data.groups || []).length) toast.info('No minted USNs to print yet — tag some bundles first');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load USNs'); }
    finally { setLoadingPrint(false); }
  };

  const markReady = async () => {
    setMarking(true);
    try {
      await apiClient.post(`/flexi/jw-orders/${jwId}/mark-ready`);
      toast.success('Marked Ready for Outward');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setMarking(false); }
  };

  const dispatch = async () => {
    setMarking(true);
    try {
      await apiClient.post(`/flexi/jw-orders/${jwId}/dispatch-to-jobber`, {});
      toast.success('Dispatched to jobber');
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setMarking(false); }
  };

  if (!order) return <div className="p-8 text-center text-gray-400">Loading…</div>;

  const overall = order.requirements.reduce((acc, r) => ({
    req: acc.req + r.required_qty, tg: acc.tg + r.tagged_qty,
  }), { req: 0, tg: 0 });
  const isWholeBundle = !!order.whole_bundle_flow;
  const minted = order.assembly_minted_qty || 0;
  const target = order.target_qty || 0;
  const canMarkReady = isWholeBundle
    ? (target > 0 && minted >= target)
    : (overall.req > 0 && overall.tg >= overall.req);

  return (
    <StaffScanGate stationName="Flexi — JW Tagging">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="jw-tagging-station">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/flexi/hub')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">{order.jw_id}</h1>
              <p className="text-sm text-[#E8DCC8]">
                {order.assembly_item} × {order.target_qty} · {order.status}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge className="bg-blue-100 text-blue-700" data-testid="jw-progress-badge">
              {isWholeBundle ? `${minted}/${target} minted` : `${overall.tg}/${overall.req} tagged`}
            </Badge>
            {order.status === 'OPEN' && canMarkReady && (
              <Button onClick={markReady} disabled={marking}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="mark-ready-btn">
                {marking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Mark Ready
              </Button>
            )}
            {order.status === 'READY_FOR_OUTWARD' && (
              <Button onClick={dispatch} disabled={marking}
                className="bg-amber-700 hover:bg-amber-800"
                data-testid="dispatch-btn">
                {marking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Dispatch to Jobber
              </Button>
            )}
            {(order.status === 'AT_JOBBER' || order.status === 'RECEIVING') && (
              <>
                <Button onClick={() => navigate('/sections/flexi/receipt')}
                  className="bg-blue-700 hover:bg-blue-800"
                  data-testid="go-receipt-btn">
                  Receive Assembled USNs →
                </Button>
                <Button variant="outline" onClick={() => navigate('/sections/flexi/jw-report')}
                  data-testid="go-report-btn">
                  Report / Close
                </Button>
              </>
            )}
            {order.status === 'COMPLETED' && (
              <Button variant="outline" onClick={() => navigate(`/sections/flexi/assortment?jw_id=${jwId}`)}
                data-testid="go-to-assortment-btn">
                Go to Assortment →
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <JwEwbBar refNo={order.jw_id} testPrefix="jw-ewb" />
        {order.status === 'OPEN' && (
          <Card className="border-indigo-200" data-testid="bundle-scan-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Boxes className="w-4 h-4 text-indigo-600" /> Bundle Scan (auto-tag)
              </CardTitle>
              <p className="text-xs text-gray-500">
                Scan a component bundle. <b>Running Mtr / Than</b> bundles are consumed <b>whole</b> and auto-mint assembly USNs by the order's ratio. <b>Regular / Flexi</b> bundles tag every USN they hold (single-USN scan available below via "Pick").
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              <form onSubmit={tagBundle} className="flex items-center gap-2">
                <Scan className="w-4 h-4 text-gray-500" />
                <Input ref={bundleRef} value={bundleScan} onChange={(e) => setBundleScan(e.target.value)}
                  placeholder="Scan component bundle code…" className="h-10 max-w-md" disabled={taggingBundle}
                  data-testid="bundle-scan-input" />
                <Button type="submit" disabled={taggingBundle || !bundleScan.trim()} data-testid="bundle-scan-btn">
                  {taggingBundle ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tag Bundle'}
                </Button>
              </form>
              {bundleResult && (
                <div className="text-xs rounded-lg border px-3 py-2 bg-gray-50" data-testid="bundle-scan-result">
                  {bundleResult.mode === 'whole_bundle' ? (
                    <>
                      <span className="font-semibold text-emerald-700">
                        Consumed {bundleResult.consumed} {bundleResult.unit} → minted {bundleResult.minted_count} assembly USN(s)
                      </span>
                      {' '}from {bundleResult.bundle_code}
                      <div className="text-gray-600 mt-0.5">
                        {bundleResult.total_minted}/{bundleResult.target_qty} minted · {bundleResult.remaining} remaining
                        {bundleResult.flow && (
                          <Badge variant="outline" className="ml-2 text-[10px]">{bundleResult.flow}</Badge>
                        )}
                      </div>
                      {(bundleResult.warnings || []).map((w, i) => (
                        <div key={i} className="text-amber-700 mt-0.5">⚠ {w}</div>
                      ))}
                    </>
                  ) : (
                    <>
                      <span className="font-semibold text-emerald-700">{bundleResult.tagged_count} tagged</span>
                      {' '}from {bundleResult.bundle_code} ({bundleResult.bundle_usn_count} USNs in bundle)
                      {(bundleResult.warnings || []).map((w, i) => (
                        <div key={i} className="text-amber-700 mt-0.5">⚠ {w}</div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        <Card data-testid="jw-usn-print-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="w-4 h-4 text-indigo-600" /> Print USN Stickers
              </CardTitle>
              <Button size="sm" variant="outline" onClick={loadPrintUsns} disabled={loadingPrint} data-testid="load-usns-btn">
                {loadingPrint ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Tag className="w-4 h-4 mr-1" />} Load USNs
              </Button>
            </div>
            <p className="text-xs text-gray-500">Print the minted Flexi USN stickers, grouped by tagged bundle</p>
          </CardHeader>
          <CardContent>
            <UsnStickerPrinter groups={printGroups} screenKey="flexi-jw-usn" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="w-4 h-4" /> Component Requirements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead>Required</TableHead>
                  <TableHead>Tagged</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {order.requirements.map((r) => (
                  <TableRow key={r.component_item}
                    data-testid={`req-row-${r.component_item}`}>
                    <TableCell className="font-mono text-xs">{r.component_item}</TableCell>
                    <TableCell>{r.required_qty}</TableCell>
                    <TableCell>{r.tagged_qty}</TableCell>
                    <TableCell>
                      {r.tagged_qty >= r.required_qty
                        ? <Badge className="bg-emerald-100 text-emerald-700">Done</Badge>
                        : <Badge variant="secondary">{r.required_qty - r.tagged_qty} left</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm"
                        variant={activeComponent === r.component_item ? 'default' : 'outline'}
                        disabled={r.tagged_qty >= r.required_qty || order.status !== 'OPEN'}
                        onClick={() => setActiveComponent(r.component_item)}
                        data-testid={`activate-${r.component_item}`}>
                        {activeComponent === r.component_item ? 'Scanning' : 'Pick'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {activeComponent && order.status === 'OPEN' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Scanning for: <span className="font-mono">{activeComponent}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Bundle code (optional)</label>
                <Input value={bundleCode} onChange={(e) => setBundleCode(e.target.value)}
                  className="max-w-md" placeholder="BUN-…"
                  data-testid="bundle-code-input" />
              </div>
              <form onSubmit={scan} className="flex items-center gap-2">
                <Scan className="w-4 h-4 text-gray-500" />
                <Input ref={usnRef} value={usn}
                  onChange={(e) => setUsn(e.target.value)}
                  placeholder="Scan original USN of the component piece…"
                  className="h-10 max-w-md" disabled={scanning}
                  data-testid="tag-usn-input" />
                <Button type="submit" disabled={scanning}
                  data-testid="tag-usn-submit">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tag'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Replace USN log ({replacements.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead>Original USN</TableHead>
                  <TableHead>Replace USN</TableHead>
                  <TableHead>Tagged At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {replacements.slice().reverse().map((r) => (
                  <TableRow key={r.new_usn}>
                    <TableCell className="font-mono text-[11px]">{r.component_item}</TableCell>
                    <TableCell className="font-mono text-[11px]">{r.original_usn}</TableCell>
                    <TableCell className="font-mono text-[11px]">{r.new_usn}</TableCell>
                    <TableCell className="text-[11px] text-gray-500">
                      {r.tagged_at ? new Date(r.tagged_at).toLocaleString('en-IN') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
    </StaffScanGate>
  );
};

export default JwTaggingStation;
